import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContainer, ProgressCircle, ToastQueue, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useReefConfig } from '../../../../hooks/UseReefConfig';
import { ListDocumentRequest } from '../../../../services/soap/document_library/requests/ListDocumentRequest';
import { UpdateInvoiceRequest } from '../../../../services/soap/invoice/requests/UpdateInvoiceRequest';
import { buildInvoicePayloadForSave } from '../../../../shared/invoice/transactionBuilder';
import { invokeNewMessageCreating } from '../../../../utils/NavigationUtils';
import DocumentPreviewComponent, { PreviewDocument } from '../../../../components/protected/layout-components/DocumentPreview/DocumentPreviewComponent';
import InvoiceDocumentUploadDialog from './InvoiceDocumentUploadDialog';
import { DeleteConfirmationDialog } from '../../finder/components/DeleteConfirmationDialog';
import { FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { useTranslation } from 'react-i18next';

interface IInvoiceDocumentsViewProps {
  invoice: any;
  refreshKey?: number;
  onUploadDocument?: () => void;
  onRefreshInvoice?: () => Promise<void> | void;
}

const normalizeInvoiceDocuments = (documentValue: any): PreviewDocument[] => {
  if (!documentValue) return [];
  const documents = Array.isArray(documentValue) ? documentValue : [documentValue];
  return documents
    .map((item: any) => ({
      documentId: String(item?.documentId || item?.id || ''),
      name: String(item?.name || item?.fileName || ''),
      fileName: String(item?.fileName || item?.name || ''),
      mimeContentType: String(item?.mimeContentType || ''),
    }))
    .filter((item) => item.documentId);
};

const mergeDocuments = (...collections: PreviewDocument[][]): PreviewDocument[] => {
  const map = new Map<string, PreviewDocument>();
  collections.flat().forEach((document) => {
    if (!document?.documentId) return;
    map.set(String(document.documentId), document);
  });
  return Array.from(map.values());
};

const InvoiceDocumentsView: React.FC<IInvoiceDocumentsViewProps> = ({ invoice, refreshKey = 0, onUploadDocument, onRefreshInvoice }) => {
  const { baseUrl } = useReefConfig();
  const { documentLibraryService, invoiceService, store } = useDependency();
  const { t } = useTranslation('invoice');
  const [showLoader, setShowLoader] = useState(true);
  const [documents, setDocuments] = useState<PreviewDocument[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const [documentToDelete, setDocumentToDelete] = useState<PreviewDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fallbackDocuments = useMemo(() => normalizeInvoiceDocuments(invoice?.DOCUMENT), [invoice?.DOCUMENT]);

  useEffect(() => {
    let isActive = true;

    const loadDocuments = async () => {
      if (!invoice?.id) {
        if (isActive) {
          setDocuments(fallbackDocuments);
          setShowLoader(false);
        }
        return;
      }

      setShowLoader(true);
      try {
        const response = await documentLibraryService.listDocument(
          new ListDocumentRequest(store.Server, store.SessionId, {
            limit: 100,
            offset: 0,
            parent: String(invoice.id),
            orderBy: 'doc_file_name',
          })
        );

        const listedDocuments = Array.isArray(response?.DOCUMENT)
          ? response.DOCUMENT
          : response?.DOCUMENT
            ? [response.DOCUMENT]
            : [];

        const normalized = listedDocuments
          .map((item: any) => ({
            documentId: String(item?.id || item?.documentId || ''),
            name: String(item?.name || item?.fileName || ''),
            fileName: String(item?.fileName || item?.name || ''),
            mimeContentType: String(item?.mimeContentType || ''),
          }))
          .filter((item: PreviewDocument) => item.documentId);

        if (isActive) {
          setDocuments(mergeDocuments(fallbackDocuments, normalized));
        }
      } catch {
        if (isActive) {
          setDocuments(mergeDocuments(fallbackDocuments));
        }
      } finally {
        if (isActive) {
          setShowLoader(false);
        }
      }
    };

    loadDocuments();
    return () => {
      isActive = false;
    };
  }, [documentLibraryService, fallbackDocuments, invoice?.id, refreshKey, internalRefreshKey, store.Server, store.SessionId]);

  const handleSendDocument = async () => {
    try {
      const attachments = await Promise.all(
        documents.map(async (document) => {
          const response = await fetch(
            `${baseUrl}/asp-cgi/doc-download.cgi?instance=${store.Server}&session=${store.SessionId}&documentId=${document.documentId}&disposition=inline`
          );
          const blob = await response.blob();
          const base64Files = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          return {
            fileName: document.fileName || document.name,
            fileSize: blob.size,
            base64Files,
          };
        })
      );

      const activityId = String(invoice?.activity || '').trim();
      const projectId = String(invoice?.project || '').trim();
      const folderId = String(invoice?.folder || '').trim();

      const messageTarget = activityId
        ? {
            contextId: activityId,
            contextType: FPADataTypes.ACTIVITY,
            isFolderContext: false,
          }
        : projectId
          ? {
              contextId: projectId,
              contextType: FPADataTypes.PROJECT,
              isFolderContext: false,
            }
          : folderId
            ? {
                contextId: folderId,
                contextType: FPADataTypes.FOLDER,
                isFolderContext: true,
              }
            : {
                contextId: '',
                contextType: '',
                isFolderContext: true,
              };

      invokeNewMessageCreating(
        messageTarget.contextId,
        messageTarget.contextType,
        undefined,
        undefined,
        messageTarget.isFolderContext,
        attachments
      );
    } catch {
      ToastQueue.negative(
        t('unable_to_prepare_documents_for_sending', {
          ns: 'layout_components',
          defaultValue: 'Unable to prepare documents for sending.',
        }),
        { timeout: 2000 },
      );
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete?.documentId) return;

    try {
      setIsDeleting(true);
      const remainingDocuments = normalizeInvoiceDocuments(invoice?.DOCUMENT).filter(
        (item) => String(item.documentId) !== String(documentToDelete.documentId)
      );

      const basePayload = {
        ...invoice,
        id: String(invoice?.id || ''),
        DOCUMENT: remainingDocuments,
      };

      const { payload, errors } = buildInvoicePayloadForSave(basePayload, { isCreate: false });

      if (errors.length > 0) {
        ToastQueue.negative(errors[0], { timeout: 2500 });
        return;
      }

      const response = await invoiceService.updateInvoice(
        new UpdateInvoiceRequest(store.Server, store.SessionId, payload)
      );

      if (response?.result !== 'OK') {
        ToastQueue.negative(
          response?.EXCEPTION?.message ||
            response?.EXCEPTION?.reason ||
            t('unable_to_delete_document', {
              ns: 'layout_components',
              defaultValue: 'Unable to delete document.',
            }),
          {
            timeout: 2500,
          },
        );
        return;
      }

      await onRefreshInvoice?.();
      setInternalRefreshKey((prev) => prev + 1);
      ToastQueue.positive(
        t('document_deleted_successfully', {
          ns: 'layout_components',
          defaultValue: 'Document deleted successfully.',
        }),
        { timeout: 2000 },
      );
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t('unable_to_delete_document', {
            ns: 'layout_components',
            defaultValue: 'Unable to delete document.',
          }),
        { timeout: 2500 },
      );
    } finally {
      setIsDeleting(false);
      setDocumentToDelete(null);
    }
  };

  if (showLoader) {
    return (
      <View UNSAFE_style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <ProgressCircle aria-label={t('loading_documents', { ns: 'invoice', defaultValue: 'Loading documents' })} isIndeterminate />
      </View>
    );
  }

  return (
    <>
      <DocumentPreviewComponent
        documents={documents}
        baseUrl={baseUrl}
        server={store.Server}
        session={store.SessionId}
        onSendDocument={handleSendDocument}
        onUploadDocument={onUploadDocument || (() => setShowUploadDialog(true))}
        onDeleteDocument={(document) => setDocumentToDelete(document)}
      />
      <InvoiceDocumentUploadDialog
        isOpen={showUploadDialog}
        invoice={invoice}
        onClose={() => setShowUploadDialog(false)}
        onUploaded={async () => {
          await onRefreshInvoice?.();
          setInternalRefreshKey((prev) => prev + 1);
        }}
      />
      <DeleteConfirmationDialog
        isOpen={!!documentToDelete && !isDeleting}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteDocument}
        itemName={documentToDelete?.name || documentToDelete?.fileName || 'document'}
      />
      <DialogContainer onDismiss={() => undefined}>
        {isDeleting ? (
          <Dialog>
            <View padding="size-200">
              <ProgressCircle aria-label={t('deleting_document', { ns: 'invoice', defaultValue: 'Deleting document' })} isIndeterminate />
            </View>
          </Dialog>
        ) : null}
      </DialogContainer>
    </>
  );
};

export default InvoiceDocumentsView;
